import React, { useEffect, useState } from "react";
import client, { apiErr } from "../lib/api";
import { Button, Card, Badge, Modal } from "../components/ui";
import { RegulatoryBundleButton } from "../components/DataImport";
import { FileArchive, Send, Clock, Search, ShieldCheck, Download, Loader2, X, Calendar, CheckCircle2, AlertTriangle, MapPin, UserPlus, UserX, Mail, RefreshCw, Anchor, PenTool, HelpCircle, Users, Key, RotateCw, Trash2, Edit3, Plus, ZoomIn } from "lucide-react";
import { LANGS } from "../i18n";
import { TL } from "../i18n";


// ==================== Provable Timeline ====================
// Chain-of-custody strength per bundle:
//   anchored  — Ed25519-signed AND RFC 3161-timestamped (green)
//   signed    — Ed25519-signed (no notary anchor)      (blue)
//   orphaned  — no signature, MANIFEST.txt only        (gray)
function ProvableTimelinePanel() {
  const [data, setData] = useState({ events: [], counts: { anchored: 0, signed: 0, orphaned: 0 }, span: null, current_key_id: "" });
  const [busy, setBusy] = useState(false);
  const [hover, setHover] = useState(null);
  const [zoom, setZoom] = useState({ from: "", to: "" }); // ISO date strings (yyyy-mm-dd), empty = full range
  const load = React.useCallback(() => {
    setBusy(true);
    client.get("/admin/reports-compliance/timeline?limit=500")
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setBusy(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const STATUS = {
    anchored: { color: "#24A148", icon: Anchor, label: "Anchored (signed + RFC 3161 notary)" },
    signed:   { color: "#0F62FE", icon: PenTool, label: "Signed (Ed25519, no notary anchor)" },
    orphaned: { color: "#8D8D8D", icon: HelpCircle, label: "Orphaned (MANIFEST.txt only, no signature)" },
  };

  // Filter events to the zoom window (inclusive).
  const inZoom = (iso) => {
    if (!zoom.from && !zoom.to) return true;
    const t = new Date(iso).getTime();
    if (zoom.from && t < new Date(zoom.from + "T00:00:00Z").getTime()) return false;
    if (zoom.to && t > new Date(zoom.to + "T23:59:59Z").getTime()) return false;
    return true;
  };
  const filtered = data.events.filter((e) => inZoom(e.created_at));
  const visibleCounts = filtered.reduce((acc, e) => { acc[e.chain_status] = (acc[e.chain_status] || 0) + 1; return acc; }, { anchored: 0, signed: 0, orphaned: 0 });

  // Compute x-position as % across the visible range based on created_at
  let minT = null, maxT = null;
  if (filtered.length >= 2) {
    minT = new Date(filtered[0].created_at).getTime();
    maxT = new Date(filtered[filtered.length - 1].created_at).getTime();
    if (maxT === minT) maxT = minT + 1;
  } else if (filtered.length === 1) {
    minT = new Date(filtered[0].created_at).getTime();
    maxT = minT + 1;
  }
  const xPct = (ts) => {
    if (minT === null) return 50;
    return 2 + 96 * (new Date(ts).getTime() - minT) / (maxT - minT);
  };

  // Preset quick-jumps (last 90 / 30 days).
  const jumpTo = (days) => {
    const to = new Date();
    const from = new Date(Date.now() - days * 86400e3);
    setZoom({ from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) });
  };

  return (
    <Card className="space-y-4" data-testid="provable-timeline-panel">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 font-head text-lg"><Anchor size={18} className="text-[var(--primary)]" /> Provable timeline</div>
        <span className="text-xs opacity-60">chain-of-custody per bundle</span>
        <Button variant="ghost" className="ms-auto" onClick={load} disabled={busy} data-testid="provable-timeline-refresh">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
        </Button>
      </div>
      <p className="text-sm opacity-60">
        Every bundle you've ever generated, plotted in chronological order and colour-coded by the strength of the proof it carries.
        Hover any dot for the bundle ID, signature key and (if anchored) the RFC 3161 timestamp.
      </p>

      {/* Legend + counts */}
      <div className="flex flex-wrap items-center gap-3 text-xs">
        {Object.entries(STATUS).map(([k, v]) => (
          <div key={k} className="flex items-center gap-1.5" data-testid={`timeline-legend-${k}`}>
            <span className="inline-block h-3 w-3 rounded-full" style={{ background: v.color }} />
            <span>{v.label}</span>
            <span className="opacity-60">· {visibleCounts?.[k] || 0}{(zoom.from || zoom.to) ? ` / ${data.counts?.[k] || 0}` : ""}</span>
          </div>
        ))}
        {data.current_key_id && (
          <div className="ms-auto opacity-60" data-testid="timeline-current-key">
            current key: <code className="font-mono">{data.current_key_id}</code>
          </div>
        )}
      </div>

      {/* Zoom / date-range brush */}
      <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="timeline-zoom-controls">
        <ZoomIn size={14} className="opacity-60" />
        <span className="opacity-60">Zoom to</span>
        <input type="date" value={zoom.from} onChange={(e) => setZoom({ ...zoom, from: e.target.value })}
          className="rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-2 py-1"
          data-testid="timeline-zoom-from" />
        <span className="opacity-60">→</span>
        <input type="date" value={zoom.to} onChange={(e) => setZoom({ ...zoom, to: e.target.value })}
          className="rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-2 py-1"
          data-testid="timeline-zoom-to" />
        <Button variant="ghost" onClick={() => jumpTo(30)} data-testid="timeline-zoom-30d">Last 30d</Button>
        <Button variant="ghost" onClick={() => jumpTo(90)} data-testid="timeline-zoom-90d">Last quarter</Button>
        <Button variant="ghost" onClick={() => jumpTo(365)} data-testid="timeline-zoom-365d">Last year</Button>
        {(zoom.from || zoom.to) && (
          <Button variant="ghost" onClick={() => setZoom({ from: "", to: "" })} data-testid="timeline-zoom-reset">
            <X size={12} /> Reset
          </Button>
        )}
        <span className="opacity-50 ms-auto">Showing {filtered.length} of {data.events.length} bundles</span>
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-md border border-dashed border-black/10 dark:border-white/10 p-6 text-sm opacity-60 text-center" data-testid="provable-timeline-empty">
          {data.events.length === 0
            ? "No bundles yet. Download a regulatory bundle or a Board-Ready Snapshot above and they'll appear here."
            : "No bundles in this date range. Widen the zoom window or reset it."}
        </div>
      ) : (
        <div className="relative select-none" data-testid="provable-timeline-track">
          {/* Time-axis track */}
          <div className="relative h-24 rounded-md border border-black/10 dark:border-white/10 bg-black/[0.02] dark:bg-white/[0.03] overflow-hidden">
            {/* Base line */}
            <div className="absolute left-[2%] right-[2%] top-1/2 h-px bg-black/20 dark:bg-white/20" />
            {/* Dots */}
            {filtered.map((e, idx) => {
              const s = STATUS[e.chain_status] || STATUS.orphaned;
              const Icon = s.icon;
              const left = xPct(e.created_at);
              const isHover = hover === idx;
              return (
                <button
                  key={e.bundle_id + idx}
                  type="button"
                  onMouseEnter={() => setHover(idx)}
                  onMouseLeave={() => setHover((h) => (h === idx ? null : h))}
                  onFocus={() => setHover(idx)}
                  onBlur={() => setHover((h) => (h === idx ? null : h))}
                  className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full transition-transform hover:scale-125 focus:scale-125 outline-none focus:ring-2 focus:ring-[var(--primary)]/60"
                  style={{ left: `${left}%`, top: "50%",
                           width: e.kind === "board_snapshot" ? 16 : 12,
                           height: e.kind === "board_snapshot" ? 16 : 12,
                           background: s.color,
                           boxShadow: isHover ? `0 0 0 4px ${s.color}33` : "none",
                           border: e.kind === "board_snapshot" ? "2px solid #fff" : "1px solid rgba(255,255,255,0.6)" }}
                  title={`${e.bundle_id} · ${s.label}`}
                  data-testid={`timeline-dot-${e.bundle_id}`}
                >
                  <Icon size={e.kind === "board_snapshot" ? 9 : 7} className="opacity-0" aria-hidden />
                </button>
              );
            })}
            {/* Span labels */}
            <div className="absolute left-2 bottom-1 text-[10px] opacity-60" data-testid="timeline-span-from">
              {filtered.length ? new Date(filtered[0].created_at).toLocaleDateString() : ""}
            </div>
            <div className="absolute right-2 bottom-1 text-[10px] opacity-60" data-testid="timeline-span-to">
              {filtered.length ? new Date(filtered[filtered.length - 1].created_at).toLocaleDateString() : ""}
            </div>
          </div>

          {/* Hover detail card */}
          {hover != null && filtered[hover] && (
            <div className="mt-3 rounded-md border border-black/10 dark:border-white/10 bg-white/60 dark:bg-black/40 p-3 text-sm" data-testid="timeline-detail-card">
              {(() => {
                const e = filtered[hover];
                const s = STATUS[e.chain_status] || STATUS.orphaned;
                const Icon = s.icon;
                return (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs text-white" style={{ background: s.color }}>
                      <Icon size={12} /> {e.chain_status}
                    </span>
                    <code className="text-xs">{e.bundle_id}</code>
                    <span className="opacity-70 text-xs">{new Date(e.created_at).toLocaleString()}</span>
                    <Badge color={e.kind === "board_snapshot" ? "success" : "info"}>{e.kind}</Badge>
                    <Badge color="neutral">{e.files || "?"} files</Badge>
                    {e.jurisdiction && <Badge color="warn">{e.jurisdiction} · {e.filing_name}</Badge>}
                    {e.key_id && (
                      <span className="opacity-70 text-xs">key <code className="font-mono">{e.key_id}</code>{e.key_current ? " (current)" : " (rotated)"}</span>
                    )}
                    {e.notary_anchored_at && (
                      <span className="opacity-70 text-xs">anchored {new Date(e.notary_anchored_at).toLocaleString()}</span>
                    )}
                    <span className="ms-auto opacity-70 text-xs">{(e.types || []).join(", ")}</span>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}


// ==================== Board Recipient Lists ====================
function BoardRecipientListsPanel({ onListsChange }) {
  const [lists, setLists] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showEdit, setShowEdit] = useState(null); // null | list obj | "new"
  const [err, setErr] = useState("");

  const load = React.useCallback(() => {
    setBusy(true);
    client.get("/admin/board-recipient-lists")
      .then((r) => { setLists(r.data?.lists || []); onListsChange && onListsChange(r.data?.lists || []); })
      .catch((e) => setErr(apiErr(e)))
      .finally(() => setBusy(false));
  }, [onListsChange]);
  useEffect(() => { load(); }, [load]);

  const remove = async (l) => {
    if (!window.confirm(`Delete the "${l.name}" distribution list? Any schedules using it will fall back to their single recipient (if any).`)) return;
    try { await client.delete(`/admin/board-recipient-lists/${l.id}`); load(); } catch (e) { setErr(apiErr(e)); }
  };

  return (
    <Card className="space-y-3" data-testid="board-recipients-panel">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 font-head text-lg"><Users size={18} className="text-[var(--primary)]" /> Board recipient lists</div>
        <Button className="ms-auto" onClick={() => setShowEdit("new")} data-testid="board-recipients-add"><Plus size={15} /> New list</Button>
      </div>
      <p className="text-sm opacity-60">
        Save your board and audit-committee distribution lists (chair, treasurer, secretary…). Point a Bundle-scheduler entry at a list to email the same signed pack to everyone on it — each recipient gets their own personalised copy.
      </p>
      {err && <div className="rounded-md border border-[#DA1E28]/40 bg-[#DA1E28]/10 px-3 py-2 text-sm text-[#DA1E28]" data-testid="board-recipients-error">{err}</div>}
      {lists.length === 0 ? (
        <div className="rounded-md border border-dashed border-black/10 dark:border-white/10 p-4 text-sm opacity-60 text-center" data-testid="board-recipients-empty">
          No distribution lists yet. Create one to email each Board-Ready Snapshot to your full board in one click.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="board-recipients-list">
          {lists.map((l) => (
            <li key={l.id} className="rounded-md border border-black/10 dark:border-white/10 p-2" data-testid={`board-recipient-list-${l.id}`}>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-medium">{l.name}</span>
                <Badge color="info">{l.recipients.length} recipient{l.recipients.length === 1 ? "" : "s"}</Badge>
                <span className="opacity-60 text-xs">updated {new Date(l.updated_at).toLocaleString()}</span>
                <div className="ms-auto flex items-center gap-1">
                  <Button variant="ghost" onClick={() => setShowEdit(l)} data-testid={`board-recipient-edit-${l.id}`}><Edit3 size={13} /> Edit</Button>
                  <Button variant="ghost" onClick={() => remove(l)} data-testid={`board-recipient-delete-${l.id}`}><Trash2 size={13} /> Delete</Button>
                </div>
              </div>
              <div className="mt-1 flex flex-wrap gap-1 text-xs opacity-70">
                {l.recipients.map((r, i) => (
                  <span key={i} className="rounded-md border border-black/10 dark:border-white/10 px-1.5 py-0.5">
                    {r.name || r.email}{r.role ? ` · ${r.role}` : ""}
                  </span>
                ))}
              </div>
            </li>
          ))}
        </ul>
      )}
      {showEdit && (
        <BoardRecipientListEditor initial={showEdit === "new" ? null : showEdit}
          onClose={() => setShowEdit(null)}
          onSaved={() => { setShowEdit(null); load(); }} />
      )}
    </Card>
  );
}

function BoardRecipientListEditor({ initial, onClose, onSaved }) {
  const [name, setName] = useState(initial?.name || "");
  const [rows, setRows] = useState(initial?.recipients?.length
    ? initial.recipients.map((r) => ({ name: r.name || "", email: r.email || "", role: r.role || "" }))
    : [{ name: "", email: "", role: "Chair" }, { name: "", email: "", role: "Treasurer" }, { name: "", email: "", role: "Secretary" }]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const validEmail = (e) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e);
  const canSave = name.trim() && rows.length && rows.every((r) => validEmail(r.email));

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const body = { name: name.trim(),
        recipients: rows.map((r) => ({ name: r.name.trim(), email: r.email.trim(), role: r.role.trim() || null })) };
      if (initial) await client.patch(`/admin/board-recipient-lists/${initial.id}`, body);
      else await client.post("/admin/board-recipient-lists", body);
      onSaved && onSaved();
    } catch (e) { setErr(apiErr(e)); }
    setBusy(false);
  };

  return (
    <Modal open={true} onClose={onClose} title={initial ? `Edit list — ${initial.name}` : "New distribution list"} testid="board-recipient-editor">
      <div className="space-y-3">
        <div>
          <div className="mb-1 text-sm font-medium">List name</div>
          <input value={name} onChange={(e) => setName(e.target.value)} data-testid="board-recipient-name"
            placeholder="e.g. Q3 Board · Full members" maxLength={120}
            className="w-full rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 text-sm" />
        </div>
        <div className="space-y-2" data-testid="board-recipient-rows">
          <div className="text-sm font-medium">Recipients</div>
          {rows.map((r, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`board-recipient-row-${i}`}>
              <input value={r.name} onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                placeholder="Full name" className="flex-1 min-w-[140px] rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-2 py-1 text-sm"
                data-testid={`board-recipient-row-name-${i}`} />
              <input type="email" value={r.email} onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, email: e.target.value } : x))}
                placeholder="email@domain.com" className="flex-1 min-w-[180px] rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-2 py-1 text-sm"
                data-testid={`board-recipient-row-email-${i}`} />
              <input value={r.role} onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, role: e.target.value } : x))}
                placeholder="Role (Chair, Treasurer…)" className="w-40 rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-2 py-1 text-sm"
                data-testid={`board-recipient-row-role-${i}`} />
              <Button variant="ghost" onClick={() => setRows(rows.filter((_, j) => j !== i))}
                disabled={rows.length <= 1} data-testid={`board-recipient-row-remove-${i}`}>
                <X size={13} />
              </Button>
            </div>
          ))}
          <Button variant="ghost" onClick={() => setRows([...rows, { name: "", email: "", role: "" }])}
            data-testid="board-recipient-row-add"><Plus size={13} /> Add recipient</Button>
        </div>
        {err && <div className="text-sm text-red-500" data-testid="board-recipient-editor-error">{err}</div>}
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onClose} data-testid="board-recipient-editor-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy || !canSave} data-testid="board-recipient-editor-save">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} {initial ? "Save list" : "Create list"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}


// ==================== Ed25519 Signing Key ====================
function SigningKeyPanel() {
  const [data, setData] = useState({ current: null, retired: [] });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = React.useCallback(() => {
    client.get("/admin/signing-keys").then((r) => setData(r.data || { current: null, retired: [] })).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const rotate = async () => {
    if (!window.confirm(
      "Rotate the Ed25519 signing key?\n\n" +
      "• New bundles from this moment on will be signed with the new key.\n" +
      "• Historic bundles remain fully verifiable offline — each one carries its own public key inside SIGNATURE.txt.\n" +
      "• The old key is preserved in the retired list for audit."
    )) return;
    setBusy(true); setMsg("");
    try {
      const r = await client.post("/admin/signing-key/rotate", {});
      setMsg(`✓ Rotated ${r.data.previous_key_id || "(none)"} → ${r.data.new_key_id}. Historic bundles are unaffected.`);
      load();
    } catch (e) { setMsg(apiErr(e)); }
    setBusy(false);
  };

  return (
    <Card className="space-y-3" data-testid="signing-key-panel">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 font-head text-lg"><Key size={18} className="text-[var(--primary)]" /> Ed25519 signing key</div>
        <Button className="ms-auto" onClick={rotate} disabled={busy} data-testid="signing-key-rotate">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <RotateCw size={15} />} Rotate now
        </Button>
      </div>
      <p className="text-sm opacity-60">
        Every Board-Ready Snapshot is Ed25519-signed by the platform key. Rotate the key when a member of the ops team leaves, on an annual cadence, or after a suspected exposure. <b>Old bundles keep verifying</b> because each SIGNATURE.txt embeds its own public key — the /verify page needs no server lookup.
      </p>
      {msg && <div className="rounded-md border border-black/10 dark:border-white/10 px-3 py-2 text-sm" data-testid="signing-key-msg">{msg}</div>}
      {data.current && (
        <div className="rounded-md border border-[var(--primary)]/40 bg-[var(--primary)]/5 p-3 text-sm" data-testid="signing-key-current">
          <div className="flex flex-wrap items-center gap-2">
            <Badge color="success">current</Badge>
            <span className="font-medium">key_id</span>
            <code className="font-mono">{data.current.key_id}</code>
            <span className="opacity-60 text-xs">created {data.current.created_at ? new Date(data.current.created_at).toLocaleString() : "(bootstrap)"}</span>
            {data.current.rotated_from && (
              <span className="opacity-60 text-xs">rotated from <code className="font-mono">{data.current.rotated_from}</code>{data.current.rotated_by ? ` by ${data.current.rotated_by}` : ""}</span>
            )}
          </div>
          <div className="mt-1 flex items-center gap-2 text-xs opacity-70">
            <span>public key (base64):</span>
            <code className="font-mono break-all">{data.current.public_key}</code>
          </div>
        </div>
      )}
      {data.retired.length > 0 && (
        <div className="space-y-1" data-testid="signing-key-retired">
          <div className="text-sm font-medium">Retired keys ({data.retired.length})</div>
          <ul className="space-y-1">
            {data.retired.map((k) => (
              <li key={k.key_id} className="flex flex-wrap items-center gap-2 rounded-md border border-black/10 dark:border-white/10 p-2 text-xs" data-testid={`signing-key-retired-${k.key_id}`}>
                <Badge color="neutral">retired</Badge>
                <code className="font-mono">{k.key_id}</code>
                <span className="opacity-60">created {k.created_at ? new Date(k.created_at).toLocaleString() : "?"}</span>
                <span className="opacity-60">retired {k.retired_at ? new Date(k.retired_at).toLocaleString() : "?"}</span>
                {k.retired_by && <span className="opacity-60">by {k.retired_by}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}


// ==================== Delegation Roster ====================
function DelegationRosterPanel() {
  const [data, setData] = useState({ runners: [], total_delegations: 0, unique_runners: 0 });
  const [busy, setBusy] = useState(false);
  const load = React.useCallback(() => {
    setBusy(true);
    client.get("/admin/delegation-roster")
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setBusy(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <Card className="space-y-3" data-testid="delegation-roster-panel">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 font-head text-lg"><Mail size={18} className="text-[var(--primary)]" /> Delegation roster</div>
        <span className="text-xs opacity-60">
          {data.unique_runners} runner{data.unique_runners === 1 ? "" : "s"} · {data.total_delegations} assignment{data.total_delegations === 1 ? "" : "s"}
        </span>
        <Button variant="ghost" className="ms-auto" onClick={load} disabled={busy} data-testid="delegation-roster-refresh">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
        </Button>
      </div>
      <p className="text-sm opacity-60">Every external advisor currently on the hook, grouped by email. Shows earliest-due filing first so you can see who's about to be nagged.</p>
      {data.runners.length === 0 ? (
        <div className="rounded-md border border-dashed border-black/10 dark:border-white/10 p-4 text-sm opacity-60 text-center" data-testid="delegation-roster-empty">
          No delegations yet. Assign filings from the Compliance calendar above.
        </div>
      ) : (
        <ul className="space-y-3" data-testid="delegation-roster-list">
          {data.runners.map((r) => (
            <li key={r.assignee_email} className="rounded-md border border-black/10 dark:border-white/10 p-3" data-testid={`roster-runner-${r.assignee_email}`}>
              <div className="flex flex-wrap items-center gap-2">
                <b>{r.assignee_name}</b>
                <a href={`mailto:${r.assignee_email}`} className="text-xs opacity-70 hover:underline">&lt;{r.assignee_email}&gt;</a>
                {r.role_hint && <Badge color="neutral">{r.role_hint}</Badge>}
                <Badge color="info">{r.filings.length} filing{r.filings.length === 1 ? "" : "s"}</Badge>
                <span className="text-xs opacity-70">Next in <b>{r.earliest_days_until}d</b></span>
                {r.total_nags_sent > 0 && <span className="text-xs opacity-70">· {r.total_nags_sent} nag{r.total_nags_sent === 1 ? "" : "s"} sent</span>}
              </div>
              <div className="mt-2 flex flex-wrap gap-1" data-testid={`roster-runner-filings-${r.assignee_email}`}>
                {r.filings.map((f) => (
                  <span key={f.filing_id} className="rounded-md border border-black/10 dark:border-white/10 bg-black/[0.03] dark:bg-white/[0.05] px-2 py-0.5 text-xs" data-testid={`roster-filing-chip-${f.filing_id}`}>
                    {f.jurisdiction} · {f.filing_name} <span className="opacity-60">(in {f.days_until}d)</span>
                  </span>
                ))}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}


// ==================== Compliance Calendar ====================
function DelegateModal({ open, onClose, filing, current, onSaved }) {
  const [form, setForm] = useState({ assignee_name: "", assignee_email: "", role_hint: "accountant", notes: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    if (!open) return;
    setErr("");
    setForm({
      assignee_name: current?.assignee_name || "",
      assignee_email: current?.assignee_email || "",
      role_hint: current?.role_hint || "accountant",
      notes: current?.notes || "",
    });
  }, [open, current]);
  if (!filing) return null;
  const save = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await client.post("/admin/compliance-calendar/delegate", { ...form, filing_id: filing.id });
      onSaved?.(data); onClose?.();
    } catch (e) { setErr(apiErr(e)); }
    setBusy(false);
  };
  return (
    <Modal open={open} onClose={onClose} title={`Delegate — ${filing.filing_name}`} testid="filing-delegate-modal">
      <div className="space-y-3">
        {err && <div className="rounded-md bg-red-50 dark:bg-red-950/40 px-3 py-2 text-sm text-red-700 dark:text-red-300" data-testid="filing-delegate-error">{err}</div>}
        <p className="text-xs opacity-60">Assign this filing to a named person. They'll receive automated email nags 30, 14, and 3 days before the due date. Reassigning to a different email resets the nag cycle.</p>
        <div>
          <div className="mb-1 text-sm font-medium">Assignee name</div>
          <input value={form.assignee_name} onChange={(e) => setForm({ ...form, assignee_name: e.target.value })} data-testid="filing-delegate-name"
            placeholder="e.g. Jane Doe, CPA"
            className="w-full rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 text-sm" />
        </div>
        <div>
          <div className="mb-1 text-sm font-medium">Email</div>
          <input type="email" value={form.assignee_email} onChange={(e) => setForm({ ...form, assignee_email: e.target.value })} data-testid="filing-delegate-email"
            placeholder="jane@advisors.com"
            className="w-full rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 text-sm" />
        </div>
        <div>
          <div className="mb-1 text-sm font-medium">Role</div>
          <select value={form.role_hint} onChange={(e) => setForm({ ...form, role_hint: e.target.value })} data-testid="filing-delegate-role"
            className="rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 text-sm">
            <option value="accountant">Accountant</option>
            <option value="lawyer">Lawyer</option>
            <option value="family officer">Family officer</option>
            <option value="trustee">Trustee</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div>
          <div className="mb-1 text-sm font-medium">Notes (included in every nag email)</div>
          <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="filing-delegate-notes"
            placeholder="e.g. Coordinate with E&Y before filing."
            className="w-full rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 text-sm" />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onClose} data-testid="filing-delegate-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy || !form.assignee_name || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.assignee_email)} data-testid="filing-delegate-save">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <UserPlus size={15} />} {current ? "Update assignee" : "Assign filing"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}


// ==================== Custom Filings ====================
function CustomFilingsPanel({ onFilingsChange }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(null); // null | "new" | filing obj
  const [err, setErr] = useState("");

  const load = React.useCallback(() => {
    setBusy(true);
    client.get("/admin/compliance-calendar/custom-filings")
      .then((r) => { setRows(r.data?.filings || []); onFilingsChange && onFilingsChange(); })
      .catch((e) => setErr(apiErr(e)))
      .finally(() => setBusy(false));
  }, [onFilingsChange]);
  useEffect(() => { load(); }, [load]);

  const remove = async (f) => {
    if (!window.confirm(`Delete custom filing "${f.filing_name}"? Any delegations pointing at it will also be removed.`)) return;
    try {
      const res = await client.delete(`/admin/compliance-calendar/custom-filings/${f.id}`);
      if ((res.data?.delegations_removed || 0) > 0) {
        alert(`Removed ${res.data.delegations_removed} delegation(s) that pointed at this filing.`);
      }
      load();
    } catch (e) { setErr(apiErr(e)); }
  };

  return (
    <Card className="space-y-3" data-testid="custom-filings-panel">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 font-head text-lg"><Plus size={18} className="text-[var(--primary)]" /> Custom filings</div>
        <Badge color="neutral">{rows.length}</Badge>
        <Button variant="ghost" className="ms-auto" onClick={() => setEdit("import")} data-testid="custom-filings-import"><Download size={15} className="rotate-180" /> Import CSV</Button>
        <Button onClick={() => setEdit("new")} data-testid="custom-filings-add"><Plus size={15} /> New custom filing</Button>
      </div>
      <p className="text-sm opacity-60">
        The 14 curated filings above cover most jurisdictions. Add your own here for anything specific to your entities (Jersey trust returns, Cayman AEOI, internal board reviews…). Custom filings appear in the Compliance Calendar alongside the curated ones, can be delegated to a named runner, receive the same 30/14/3-day nag emails, and are scoped by the record types you pick.
      </p>
      {err && <div className="rounded-md border border-[#DA1E28]/40 bg-[#DA1E28]/10 px-3 py-2 text-sm text-[#DA1E28]" data-testid="custom-filings-error">{err}</div>}
      {rows.length === 0 ? (
        <div className="rounded-md border border-dashed border-black/10 dark:border-white/10 p-4 text-sm opacity-60 text-center" data-testid="custom-filings-empty">
          No custom filings yet. Click "New custom filing" to add one.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="custom-filings-list">
          {rows.map((f) => (
            <li key={f.id} className="flex flex-wrap items-center gap-2 rounded-md border border-black/10 dark:border-white/10 p-2 text-sm" data-testid={`custom-filing-${f.id}`}>
              <span className="font-medium">{f.filing_name}</span>
              <Badge color="info">{f.jurisdiction}</Badge>
              <Badge color="neutral">{f.cadence}</Badge>
              {f.cadence === "annual" && <span className="opacity-60 text-xs">Due {f.month}/{f.day}</span>}
              <span className="opacity-60 text-xs">{f.record_types.join(", ")}</span>
              <div className="ms-auto flex items-center gap-1">
                <Button variant="ghost" onClick={() => setEdit(f)} data-testid={`custom-filing-edit-${f.id}`}><Edit3 size={13} /> Edit</Button>
                <Button variant="ghost" onClick={() => remove(f)} data-testid={`custom-filing-delete-${f.id}`}><Trash2 size={13} /> Delete</Button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {edit && edit === "import" && (
        <CustomFilingImportModal
          onClose={() => setEdit(null)}
          onImported={() => { setEdit(null); load(); }} />
      )}
      {edit && edit !== "import" && (
        <CustomFilingEditor initial={edit === "new" ? null : edit}
          onClose={() => setEdit(null)}
          onSaved={() => { setEdit(null); load(); }} />
      )}
    </Card>
  );
}

function CustomFilingImportModal({ onClose, onImported }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [file, setFile] = useState(null);

  const upload = async () => {
    if (!file) return;
    setBusy(true); setErr(""); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await client.post("/admin/compliance-calendar/custom-filings/import", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
    } catch (e) { setErr(apiErr(e)); }
    setBusy(false);
  };

  return (
    <Modal open={true} onClose={onClose} title="Import custom filings (CSV)" testid="custom-filings-import-modal">
      <div className="space-y-3">
        <div className="rounded-md bg-black/5 dark:bg-white/5 p-2 text-xs">
          <b>Header row required.</b> Recognised columns (case-insensitive):
          <div className="mt-1 font-mono opacity-80">
            filing_name, jurisdiction, authority, cadence, month, day, year_offset, record_types, description
          </div>
          <div className="mt-1 opacity-70">
            <b>record_types</b> can be comma-, pipe- or semicolon-separated (e.g. <code>holdings|entities</code> or <code>persons;entities</code>).
            Invalid rows are reported inline; valid rows are still created. UTF-8, up to 512 KB.
          </div>
        </div>
        <input type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files?.[0] || null)}
          data-testid="custom-filings-import-file"
          className="text-sm" />
        {err && <div className="text-sm text-red-500" data-testid="custom-filings-import-error">{err}</div>}
        {result && (
          <div className="rounded-md border border-black/10 dark:border-white/10 p-2 text-sm" data-testid="custom-filings-import-result">
            <div><b>{result.created}</b> created, <b>{result.skipped}</b> skipped.</div>
            {result.errors?.length > 0 && (
              <ul className="mt-1 space-y-0.5 text-xs opacity-80" data-testid="custom-filings-import-errors">
                {result.errors.map((e, i) => (
                  <li key={i}>Row {e.row}: {e.reason}</li>
                ))}
              </ul>
            )}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onClose} data-testid="custom-filings-import-close">Close</Button>
          {result ? (
            <Button onClick={onImported} data-testid="custom-filings-import-done">Done — refresh list</Button>
          ) : (
            <Button onClick={upload} disabled={busy || !file} data-testid="custom-filings-import-upload">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Import
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}

function CustomFilingEditor({ initial, onClose, onSaved }) {
  const RT = ["holdings", "currencies", "liabilities", "persons", "benefits", "entities"];
  const [form, setForm] = useState(initial || {
    filing_name: "", jurisdiction: "", authority: "", cadence: "annual",
    month: 1, day: 1, year_offset: 0, record_types: ["holdings"], description: ""
  });
  const [curated, setCurated] = useState([]);
  const [tplId, setTplId] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    if (!initial) {
      client.get("/admin/compliance-calendar/curated-filings")
        .then((r) => setCurated(r.data?.filings || [])).catch(() => {});
    }
  }, [initial]);
  const applyTemplate = (id) => {
    setTplId(id);
    if (!id) return;
    const src = curated.find((c) => c.id === id);
    if (!src) return;
    // Copy every field but leave the filing_name blank of the source jurisdiction hint so the
    // admin adjusts it before saving (e.g. "Form 1040 (individual)" → "Foo Territory 1040").
    setForm({
      filing_name: src.filing_name,
      jurisdiction: src.jurisdiction,
      authority: src.authority || "",
      cadence: src.cadence,
      month: src.month || 1, day: src.day || 1,
      year_offset: src.year_offset || 0,
      record_types: [...(src.record_types || [])],
      description: src.description || "",
    });
  };
  const isAnnual = form.cadence === "annual";
  const canSave = form.filing_name.trim() && form.jurisdiction.trim() &&
    form.record_types.length &&
    (!isAnnual || (form.month >= 1 && form.month <= 12 && form.day >= 1 && form.day <= 31));

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const payload = { ...form, month: Number(form.month), day: Number(form.day),
                         year_offset: Number(form.year_offset) };
      if (initial) await client.patch(`/admin/compliance-calendar/custom-filings/${initial.id}`, payload);
      else await client.post("/admin/compliance-calendar/custom-filings", payload);
      onSaved && onSaved();
    } catch (e) { setErr(apiErr(e)); }
    setBusy(false);
  };

  return (
    <Modal open={true} onClose={onClose} title={initial ? `Edit — ${initial.filing_name}` : "New custom filing"} testid="custom-filing-editor">
      <div className="space-y-3">
        {!initial && curated.length > 0 && (
          <div className="rounded-md border border-dashed border-[var(--primary)]/40 bg-[var(--primary)]/5 p-2 text-sm" data-testid="custom-filing-template">
            <div className="mb-1 text-xs opacity-70">Copy from a curated filing (optional starting point)</div>
            <select value={tplId} onChange={(e) => applyTemplate(e.target.value)}
              className="w-full rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-2 py-1 text-sm"
              data-testid="custom-filing-template-select">
              <option value="">— Start from blank —</option>
              {curated.map((c) => (
                <option key={c.id} value={c.id}>{c.jurisdiction} · {c.filing_name}</option>
              ))}
            </select>
            {tplId && <div className="mt-1 text-xs opacity-60">Fields pre-filled from the template — adjust name, jurisdiction and dates to match your case, then Save.</div>}
          </div>
        )}
        <div>
          <div className="mb-1 text-sm font-medium">Filing name</div>
          <input value={form.filing_name} onChange={(e) => setForm({ ...form, filing_name: e.target.value })}
            placeholder="e.g. Jersey Trust Annual Return" maxLength={120}
            className="w-full rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 text-sm"
            data-testid="custom-filing-name" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="mb-1 text-sm font-medium">Jurisdiction</div>
            <input value={form.jurisdiction} onChange={(e) => setForm({ ...form, jurisdiction: e.target.value })}
              placeholder="e.g. JE-JFSC" maxLength={32}
              className="w-full rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 text-sm"
              data-testid="custom-filing-jurisdiction" />
          </div>
          <div>
            <div className="mb-1 text-sm font-medium">Authority (optional)</div>
            <input value={form.authority} onChange={(e) => setForm({ ...form, authority: e.target.value })}
              placeholder="e.g. Jersey Financial Services Commission" maxLength={120}
              className="w-full rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 text-sm"
              data-testid="custom-filing-authority" />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <div className="mb-1 text-sm font-medium">Cadence</div>
            <select value={form.cadence} onChange={(e) => setForm({ ...form, cadence: e.target.value })}
              className="w-full rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 text-sm"
              data-testid="custom-filing-cadence">
              <option value="annual">Annual</option>
              <option value="quarterly">Quarterly</option>
              <option value="monthly">Monthly</option>
              <option value="weekly">Weekly</option>
              <option value="once">One-shot</option>
            </select>
          </div>
          {isAnnual && (
            <>
              <div>
                <div className="mb-1 text-sm font-medium">Due month (1–12)</div>
                <input type="number" min="1" max="12" value={form.month}
                  onChange={(e) => setForm({ ...form, month: e.target.value })}
                  className="w-full rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 text-sm"
                  data-testid="custom-filing-month" />
              </div>
              <div>
                <div className="mb-1 text-sm font-medium">Due day (1–31)</div>
                <input type="number" min="1" max="31" value={form.day}
                  onChange={(e) => setForm({ ...form, day: e.target.value })}
                  className="w-full rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 text-sm"
                  data-testid="custom-filing-day" />
              </div>
            </>
          )}
        </div>
        <div>
          <div className="mb-1 text-sm font-medium">Record types included in filing bundle</div>
          <div className="flex flex-wrap gap-2">
            {RT.map((t) => (
              <label key={t} className={`flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${form.record_types.includes(t) ? "border-[var(--primary)] bg-[var(--primary)]/5" : "border-black/10 dark:border-white/10"}`}>
                <input type="checkbox" checked={form.record_types.includes(t)}
                  onChange={(e) => setForm({ ...form, record_types: e.target.checked
                    ? [...form.record_types, t]
                    : form.record_types.filter((x) => x !== t) })}
                  data-testid={`custom-filing-rt-${t}`} />
                {t}
              </label>
            ))}
          </div>
        </div>
        <div>
          <div className="mb-1 text-sm font-medium">Description (shown in every nag email)</div>
          <textarea rows={2} value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            maxLength={500}
            className="w-full rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 text-sm"
            data-testid="custom-filing-description" />
        </div>
        {err && <div className="text-sm text-red-500" data-testid="custom-filing-editor-error">{err}</div>}
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onClose} data-testid="custom-filing-editor-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy || !canSave} data-testid="custom-filing-editor-save">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} {initial ? "Save changes" : "Create filing"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}


// ==================== Delegation Heat-Map ====================
function DelegationHeatMapPanel() {
  const [data, setData] = useState({ runners: [], max_count: 0, total_delegations: 0,
                                       overload_thresholds: { total: 5, urgent: 3 } });
  const [busy, setBusy] = useState(false);
  const [redistFor, setRedistFor] = useState(null);
  const load = React.useCallback(() => {
    setBusy(true);
    client.get("/admin/reports-compliance/delegation-heatmap")
      .then((r) => setData(r.data || { runners: [] }))
      .catch(() => {})
      .finally(() => setBusy(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  const overloaded = data.runners.filter((r) => r.overloaded).length;
  return (
    <Card className="space-y-3" data-testid="delegation-heatmap-panel">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 font-head text-lg"><Users size={18} className="text-[var(--primary)]" /> Delegation heat-map</div>
        <Badge color="neutral">{data.total_delegations} total</Badge>
        <Badge color="info">{data.runners.length} runners</Badge>
        {overloaded > 0 && <Badge color="warn" data-testid="heatmap-overloaded-badge">{overloaded} overloaded</Badge>}
        <Button variant="ghost" className="ms-auto" onClick={load} disabled={busy} data-testid="delegation-heatmap-refresh">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
        </Button>
      </div>
      <p className="text-sm opacity-60">
        Filings per assignee, sorted by overdue → urgent → total. A runner is flagged <b>overloaded</b>
        when they carry <b>≥ {data.overload_thresholds?.total || 5}</b> filings OR <b>≥ {data.overload_thresholds?.urgent || 3}</b> urgent ones (due in ≤ 14 days). Each row also carries an <b>SLA</b> chip once you start marking filings as filed.
      </p>
      {data.runners.length === 0 ? (
        <div className="rounded-md border border-dashed border-black/10 dark:border-white/10 p-4 text-sm opacity-60 text-center" data-testid="delegation-heatmap-empty">
          No delegations yet. Assign a filing to a runner to populate this chart.
        </div>
      ) : (
        <ul className="space-y-1.5" data-testid="delegation-heatmap-list">
          {data.runners.map((r) => {
            const pct = data.max_count > 0 ? Math.max(6, (r.count / data.max_count) * 100) : 0;
            const isOver = r.overloaded;
            const barColor = r.overdue > 0 ? "#DA1E28" : (isOver ? "#F1C21B" : (r.urgent > 0 ? "#0F62FE" : "var(--primary)"));
            const sla = r.sla_rate;
            const slaColor = sla == null ? "neutral" : sla >= 90 ? "success" : sla >= 70 ? "info" : "danger";
            return (
              <li key={r.assignee_email} className="rounded-md border border-black/10 dark:border-white/10 p-2 text-sm" data-testid={`heatmap-row-${r.assignee_email}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium truncate" title={r.assignee_email}>{r.assignee_name || r.assignee_email}</span>
                  <span className="opacity-60 text-xs">&lt;{r.assignee_email}&gt;</span>
                  {r.role_hint && <Badge color="neutral">{r.role_hint}</Badge>}
                  {isOver && <Badge color="warn" data-testid={`heatmap-overloaded-${r.assignee_email}`}>overloaded</Badge>}
                  {r.overdue > 0 && <Badge color="danger" data-testid={`heatmap-overdue-${r.assignee_email}`}>{r.overdue} overdue</Badge>}
                  {r.urgent > 0 && <Badge color="info" data-testid={`heatmap-urgent-${r.assignee_email}`}>{r.urgent} urgent</Badge>}
                  {sla != null && (
                    <Badge color={slaColor} data-testid={`heatmap-sla-${r.assignee_email}`}>
                      SLA {sla}% ({r.sla_on_time}/{r.sla_completions})
                    </Badge>
                  )}
                  {isOver && data.runners.length > 1 && (
                    <Button variant="ghost" onClick={() => setRedistFor(r.assignee_email)} data-testid={`heatmap-redistribute-${r.assignee_email}`}>
                      <RefreshCw size={12} /> Redistribute
                    </Button>
                  )}
                  <span className="ms-auto text-xs opacity-70">{r.count} filing{r.count === 1 ? "" : "s"} · {(r.jurisdictions || []).length} jurisdiction{(r.jurisdictions || []).length === 1 ? "" : "s"}</span>
                </div>
                <div className="mt-1 h-3 w-full rounded-full bg-black/[0.06] dark:bg-white/[0.06] overflow-hidden" data-testid={`heatmap-bar-${r.assignee_email}`}>
                  <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: barColor }} />
                </div>
                <div className="mt-1 text-[11px] opacity-60">
                  Earliest due {r.earliest_due ? new Date(r.earliest_due).toLocaleDateString() : "—"}
                  {typeof r.earliest_days_until === "number" && ` · in ${r.earliest_days_until} day${r.earliest_days_until === 1 ? "" : "s"}`}
                  {(r.jurisdictions || []).length > 0 && ` · ${r.jurisdictions.join(", ")}`}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {redistFor && (
        <RedistributeModal email={redistFor}
          onClose={() => setRedistFor(null)}
          onCommitted={() => { setRedistFor(null); load(); }} />
      )}
    </Card>
  );
}


function RedistributeModal({ email, onClose, onCommitted }) {
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    setBusy(true);
    client.get(`/admin/reports-compliance/redistribute-plan?email=${encodeURIComponent(email)}`)
      .then((r) => setPlan(r.data))
      .catch((e) => setErr(apiErr(e)))
      .finally(() => setBusy(false));
  }, [email]);
  const commit = async () => {
    if (!plan?.moves?.length) return;
    if (!window.confirm(`Apply ${plan.moves.length} filing transfer(s)?\n\nEach transferred filing's nag history is reset so the successor gets fresh 30/14/3-day reminders.`)) return;
    setBusy(true); setErr("");
    try {
      await client.post("/admin/reports-compliance/redistribute-plan", { moves: plan.moves });
      onCommitted && onCommitted();
    } catch (e) { setErr(apiErr(e)); }
    setBusy(false);
  };
  return (
    <Modal open={true} onClose={onClose} title={`Redistribute — ${email}`} testid="redistribute-modal">
      <div className="space-y-3">
        {busy && !plan && <div className="text-sm opacity-60 text-center"><Loader2 size={14} className="inline animate-spin" /> Building plan…</div>}
        {err && <div className="text-sm text-red-500" data-testid="redistribute-error">{err}</div>}
        {plan && (
          <>
            <div className="text-xs opacity-70">
              Suggested spread: each of the <b>{plan.n_runners}</b> runner(s) ends up carrying no more than <b>{plan.target_per_runner}</b> filings.
              {plan.moves.length === 0 && <div className="mt-1 opacity-80">{plan.message || "No moves needed."}</div>}
            </div>
            {plan.moves.length > 0 && (
              <div className="rounded-md border border-[var(--primary)]/40 bg-[var(--primary)]/5 p-2 space-y-1" data-testid="redistribute-diff">
                <div className="text-xs font-medium">Proposed moves ({plan.moves.length})</div>
                <ul className="space-y-1 text-xs">
                  {plan.moves.map((m) => (
                    <li key={m.filing_id} className="flex flex-wrap items-center gap-2" data-testid={`redistribute-move-${m.filing_id}`}>
                      <span className="rounded-md border border-black/10 dark:border-white/10 px-2 py-0.5 truncate">
                        {m.filing_name} · {m.jurisdiction}
                      </span>
                      <span className="opacity-70">{m.from_email}</span>
                      <span className="opacity-40">→</span>
                      <span className="rounded-md border border-[var(--primary)]/40 bg-[var(--primary)]/10 px-2 py-0.5">
                        {m.to_name} <span className="opacity-60">&lt;{m.to_email}&gt;</span>
                      </span>
                    </li>
                  ))}
                </ul>
                <div className="text-[11px] opacity-60 pt-1">Nag history is reset on every moved filing so the new owner starts fresh.</div>
              </div>
            )}
            {plan.after && Object.keys(plan.after).length > 0 && (
              <div className="rounded-md border border-black/10 dark:border-white/10 p-2 text-xs" data-testid="redistribute-after-summary">
                <div className="mb-1 font-medium">After the redistribution</div>
                <ul className="space-y-0.5">
                  {Object.values(plan.after).sort((a, b) => b.filings.length - a.filings.length).map((r) => (
                    <li key={r.assignee_email} className="flex items-center gap-2">
                      <span className="w-40 truncate opacity-80">{r.assignee_name || r.assignee_email}</span>
                      <span className="opacity-70">{r.filings.length} filing{r.filings.length === 1 ? "" : "s"}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="ghost" onClick={onClose} data-testid="redistribute-cancel">Cancel</Button>
              <Button onClick={commit} disabled={busy || !plan.moves.length} data-testid="redistribute-commit">
                {busy ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} Commit redistribution
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}



// ==================== Bulk-Retire Delegations ====================
function BulkRetireDelegationsPanel() {
  const [email, setEmail] = useState("");
  const [preview, setPreview] = useState(null); // {filings: [], departing_name}
  const [handoffName, setHandoffName] = useState("");
  const [handoffEmail, setHandoffEmail] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const validEmail = (e) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e);

  const loadPreview = async () => {
    setMsg("");
    if (!validEmail(email)) { setMsg("Enter a valid email above first."); return; }
    setBusy(true);
    try {
      const r = await client.get("/admin/delegation-roster");
      const runner = (r.data?.runners || []).find((row) =>
        (row.assignee_email || "").toLowerCase() === email.toLowerCase());
      const filings = runner
        ? (runner.filings || []).map((f) => ({ filing_id: f.filing_id,
            filing_name: f.filing_name, jurisdiction: f.jurisdiction,
            days_until: f.days_until, is_custom: !!f.is_custom }))
        : [];
      setPreview({ filings, departing_name: runner?.assignee_name || email });
      if (filings.length === 0) setMsg("No delegations found for that email.");
    } catch (e) { setMsg(apiErr(e)); }
    setBusy(false);
  };

  const wantsReassign = !!(handoffEmail && validEmail(handoffEmail));
  const canRun = preview && preview.filings.length > 0 &&
    (!wantsReassign || (handoffName.trim() && validEmail(handoffEmail)));

  const run = async () => {
    const action = wantsReassign ? `reassign to ${handoffEmail}` : "clear from queue";
    if (!window.confirm(`${wantsReassign ? "Reassign" : "Clear"} ${preview.filings.length} delegation(s) for ${email}?\n\nAction: ${action}\nCourtesy email: ${sendEmail ? "yes" : "no"}`)) return;
    setBusy(true); setMsg("");
    try {
      const payload = { assignee_email: email, send_handoff_email: sendEmail,
                         handoff_note: note || null };
      if (wantsReassign) {
        payload.handoff_to_name = handoffName.trim();
        payload.handoff_to_email = handoffEmail.trim();
      }
      const r = await client.post("/admin/compliance-calendar/bulk-retire", payload);
      setMsg(
        `✓ ${r.data.retired} cleared, ${r.data.reassigned} reassigned. ` +
        `Courtesy email ${r.data.handoff_email_sent ? "sent" : "not sent"}.`
      );
      setPreview(null); setEmail(""); setHandoffName(""); setHandoffEmail(""); setNote("");
    } catch (e) { setMsg(apiErr(e)); }
    setBusy(false);
  };

  return (
    <Card className="space-y-3" data-testid="bulk-retire-panel">
      <div className="flex items-center gap-2 font-head text-lg"><UserX size={18} className="text-[var(--primary)]" /> Bulk-retire delegations</div>
      <p className="text-sm opacity-60">
        When a runner leaves the family office, clear every filing they're on in one click and (optionally) transfer them to a successor. A courtesy hand-off email lists every filing that was on their plate.
      </p>
      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-[240px] flex-1">
          <div className="mb-1 text-xs opacity-60">Departing runner's email</div>
          <input type="email" value={email} onChange={(e) => { setEmail(e.target.value); setPreview(null); }}
            placeholder="departing@advisors.com"
            className="w-full rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 text-sm"
            data-testid="bulk-retire-email" />
        </div>
        <Button onClick={loadPreview} disabled={busy || !validEmail(email)} data-testid="bulk-retire-preview">
          {busy && !preview ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Preview
        </Button>
      </div>

      {preview && preview.filings.length > 0 && (
        <div className="rounded-md border border-black/10 dark:border-white/10 p-3 text-sm space-y-3" data-testid="bulk-retire-preview-card">
          <div className="text-sm">
            <b>{preview.departing_name}</b> is currently on {preview.filings.length} filing{preview.filings.length === 1 ? "" : "s"}:
          </div>
          {!wantsReassign ? (
            <ul className="flex flex-wrap gap-1 text-xs" data-testid="bulk-retire-filing-list">
              {preview.filings.map((f) => (
                <li key={f.filing_id} className="rounded-md border border-black/10 dark:border-white/10 px-2 py-0.5" data-testid={`bulk-retire-filing-${f.filing_id}`}>
                  {f.filing_name} · {f.jurisdiction}{f.is_custom ? " · custom" : ""}
                </li>
              ))}
            </ul>
          ) : (
            <div className="rounded-md border border-[var(--primary)]/40 bg-[var(--primary)]/5 p-2 space-y-1" data-testid="bulk-retire-diff">
              <div className="text-xs font-medium">Before → After (on save)</div>
              <div className="grid grid-cols-[1fr_auto_1fr] gap-x-2 gap-y-1 text-xs items-center">
                <div className="opacity-60 pb-1 border-b border-black/10 dark:border-white/10">Now on <b>{preview.departing_name || email}</b></div>
                <div className="opacity-60 pb-1 border-b border-black/10 dark:border-white/10 text-center">→</div>
                <div className="opacity-60 pb-1 border-b border-black/10 dark:border-white/10">Will be on <b>{handoffName || "(successor)"}</b> <span className="opacity-60">&lt;{handoffEmail}&gt;</span></div>
                {preview.filings.map((f) => (
                  <React.Fragment key={f.filing_id}>
                    <div className="line-through opacity-60 rounded-md border border-black/10 dark:border-white/10 px-2 py-0.5 truncate" data-testid={`bulk-retire-diff-before-${f.filing_id}`}>
                      {f.filing_name} · {f.jurisdiction}
                    </div>
                    <div className="text-center opacity-40">→</div>
                    <div className="rounded-md border border-[var(--primary)]/40 bg-[var(--primary)]/10 px-2 py-0.5 truncate" data-testid={`bulk-retire-diff-after-${f.filing_id}`}>
                      {f.filing_name} · {f.jurisdiction}
                      {f.days_until <= 14 && <span className="ms-1 text-[10px] rounded px-1 bg-[#DA1E28] text-white">urgent</span>}
                    </div>
                  </React.Fragment>
                ))}
              </div>
              <div className="text-[11px] opacity-60 pt-1">Each transferred filing's nag history is reset so the successor's 30/14/3-day reminders fire fresh.</div>
            </div>
          )}
          <div className="pt-1 border-t border-black/5 dark:border-white/5">
            <div className="mb-1 text-xs opacity-60">Hand-off (optional — leave empty to just clear the queue)</div>
            <div className="flex flex-wrap gap-2">
              <input value={handoffName} onChange={(e) => setHandoffName(e.target.value)}
                placeholder="Successor name"
                className="flex-1 min-w-[160px] rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-2 py-1 text-sm"
                data-testid="bulk-retire-handoff-name" />
              <input type="email" value={handoffEmail} onChange={(e) => setHandoffEmail(e.target.value)}
                placeholder="successor@advisors.com"
                className="flex-1 min-w-[180px] rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-2 py-1 text-sm"
                data-testid="bulk-retire-handoff-email" />
            </div>
          </div>
          <div>
            <div className="mb-1 text-xs opacity-60">Optional note (appended to the hand-off email)</div>
            <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Thank you for your service…"
              className="w-full rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-2 py-1 text-sm"
              data-testid="bulk-retire-note" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)}
              data-testid="bulk-retire-send-email" />
            Send courtesy hand-off email to {email}
          </label>
          <div className="flex justify-end">
            <Button onClick={run} disabled={!canRun || busy} data-testid="bulk-retire-run">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <UserX size={14} />}
              {wantsReassign ? `Reassign all ${preview.filings.length}` : `Clear all ${preview.filings.length}`}
            </Button>
          </div>
        </div>
      )}
      {msg && <div className="rounded-md bg-black/5 dark:bg-white/5 px-3 py-2 text-sm" data-testid="bulk-retire-msg">{msg}</div>}
    </Card>
  );
}


function ComplianceCalendarPanel({ onFilingsReload }) {
  const [data, setData] = useState({ filings: [], jurisdictions: [] });
  const [jurisdiction, setJurisdiction] = useState("all");
  const [busy, setBusy] = useState(false);
  const [downloadingId, setDownloadingId] = useState("");
  const [msg, setMsg] = useState("");
  const [delegateFor, setDelegateFor] = useState(null); // filing being edited

  const load = React.useCallback(() => {
    setBusy(true);
    client.get(`/admin/compliance-calendar?jurisdiction=${jurisdiction}&window_days=365`)
      .then((r) => setData(r.data || { filings: [], jurisdictions: [] }))
      .catch((e) => setMsg(apiErr(e)))
      .finally(() => setBusy(false));
  }, [jurisdiction]);

  useEffect(() => { load(); }, [load, onFilingsReload]);

  const downloadFiling = async (f) => {
    setDownloadingId(f.id); setMsg("");
    try {
      const res = await client.get(`/admin/compliance-calendar/bundle/${f.id}`, { responseType: "blob" });
      const bid = res.headers["x-bundle-id"] || "";
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${f.jurisdiction}-${f.id}-${bid}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setMsg(`✓ Generated bundle ${bid} for ${f.filing_name} (${f.record_types.length} collections scoped).`);
    } catch (e) { setMsg(apiErr(e)); }
    setDownloadingId("");
  };

  const unassign = async (f) => {
    if (!window.confirm(`Remove ${f.assignee?.assignee_name} from ${f.filing_name}? They will stop receiving nag emails for this filing.`)) return;
    try {
      await client.delete(`/admin/compliance-calendar/delegate/${f.id}`);
      load();
    } catch (e) { setMsg(apiErr(e)); }
  };

  const markFiled = async (f) => {
    const when = window.prompt(
      `Mark "${f.filing_name}" as filed for the current cycle?\n\nType the date filed (YYYY-MM-DD) or leave blank for today.`,
      new Date().toISOString().slice(0, 10));
    if (when === null) return;
    try {
      const filed_at = when.trim() ? new Date(when + "T00:00:00Z").toISOString() : undefined;
      const r = await client.post("/admin/compliance-calendar/mark-filed",
        { filing_id: f.id, filed_at });
      const on = r.data?.on_time;
      setMsg(`✓ Marked ${f.filing_name} filed for cycle ${r.data?.cycle_key} — ${on ? "on time" : "LATE"}.`);
    } catch (e) { setMsg(apiErr(e)); }
  };

  // Colour code by urgency — red < 14d, amber < 45d, blue < 90d, gray beyond.
  const urgencyColor = (d) => {
    if (d < 0) return "border-black/20 bg-black/5 text-black/60 dark:text-white/60";
    if (d < 14) return "border-[#DA1E28]/40 bg-[#DA1E28]/10 text-[#DA1E28]";
    if (d < 45) return "border-[#F1C21B]/40 bg-[#F1C21B]/10 text-[#8A6D0B] dark:text-[#F1C21B]";
    if (d < 90) return "border-[#0F62FE]/40 bg-[#0F62FE]/10 text-[#0F62FE]";
    return "border-black/10 bg-black/5 opacity-70";
  };

  return (
    <Card className="space-y-3" data-testid="compliance-calendar-panel">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 font-head text-lg"><Calendar size={18} className="text-[var(--primary)]" /> Compliance calendar</div>
        <select value={jurisdiction} onChange={(e) => setJurisdiction(e.target.value)} data-testid="compliance-calendar-jurisdiction"
          className="ms-auto rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-2 py-1 text-sm">
          <option value="all">All jurisdictions</option>
          {data.jurisdictions.map((j) => <option key={j} value={j}>{j}</option>)}
        </select>
      </div>
      <p className="text-sm opacity-60">
        Upcoming filings (next 12 months). Click "Bundle" to auto-generate a regulatory pack scoped to that filing's exact record set, or "Delegate" to hand it to a named accountant/lawyer/family officer who will receive auto-nag emails at 30 / 14 / 3 days out.
      </p>
      {msg && <div className="rounded-md bg-black/5 dark:bg-white/5 px-3 py-2 text-xs" data-testid="compliance-calendar-msg">{msg}</div>}
      {busy && data.filings.length === 0 ? (
        <div className="p-4 text-center text-sm opacity-60"><Loader2 size={16} className="inline animate-spin" /> Loading…</div>
      ) : data.filings.length === 0 ? (
        <div className="rounded-md border border-dashed border-black/10 dark:border-white/10 p-4 text-sm opacity-60 text-center" data-testid="compliance-calendar-empty">
          No filings match that jurisdiction in the next 12 months.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="compliance-calendar-list">
          {data.filings.map((f) => (
            <li key={f.id} className={`space-y-1 rounded-md border p-2 text-sm ${urgencyColor(f.days_until)}`} data-testid={`compliance-filing-${f.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <MapPin size={14} className="shrink-0" />
                <span className="font-medium">{f.filing_name}</span>
                <Badge color="info">{f.jurisdiction}</Badge>
                <Badge color="neutral">{f.cadence}</Badge>
                {f.is_custom && <Badge color="warn" data-testid={`compliance-filing-custom-${f.id}`}>custom</Badge>}
                <span className="text-xs opacity-70">
                  Due {new Date(f.next_due).toLocaleDateString()} ·
                  <b className="ms-1">{f.days_until >= 0 ? `in ${f.days_until} day${f.days_until === 1 ? "" : "s"}` : `${Math.abs(f.days_until)} days overdue`}</b>
                </span>
                <span className="text-xs opacity-60 ms-auto">{f.record_types.join(", ")}</span>
                <Button variant="ghost" onClick={() => downloadFiling(f)} disabled={downloadingId === f.id}
                  data-testid={`compliance-filing-bundle-${f.id}`}>
                  {downloadingId === f.id ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} Bundle
                </Button>
                <Button variant="ghost" onClick={() => setDelegateFor(f)} data-testid={`compliance-filing-delegate-${f.id}`}>
                  <UserPlus size={13} /> {f.assignee ? "Reassign" : "Delegate"}
                </Button>
                <Button variant="ghost" onClick={() => markFiled(f)} data-testid={`compliance-filing-mark-filed-${f.id}`}>
                  <CheckCircle2 size={13} /> Mark filed
                </Button>
              </div>
              {f.assignee && (
                <div className="flex flex-wrap items-center gap-2 rounded-md border border-black/10 bg-black/[0.03] dark:border-white/10 dark:bg-white/[0.05] px-2 py-1 text-xs" data-testid={`compliance-filing-assignee-${f.id}`}>
                  <Mail size={12} />
                  <b>{f.assignee.assignee_name}</b>
                  <span className="opacity-70">&lt;{f.assignee.assignee_email}&gt;</span>
                  {f.assignee.role_hint && <Badge color="neutral">{f.assignee.role_hint}</Badge>}
                  {(f.assignee.notified_days || []).length > 0 && (
                    <span className="opacity-70">Nagged: {(f.assignee.notified_days || []).join("d, ")}d</span>
                  )}
                  <Button variant="ghost" className="ms-auto" onClick={() => unassign(f)} data-testid={`compliance-filing-unassign-${f.id}`}>
                    <UserX size={12} /> Unassign
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      <DelegateModal open={!!delegateFor} onClose={() => setDelegateFor(null)}
        filing={delegateFor} current={delegateFor?.assignee}
        onSaved={() => load()} />
    </Card>
  );
}


// ==================== Bundle Scheduler ====================
function BundleSchedulerPanel({ recipientLists = [] }) {
  const [schedules, setSchedules] = useState([]);
  const [available, setAvailable] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", kind: "regulatory",
                                     types: ["holdings", "currencies", "liabilities", "persons"],
                                     include_networth_pdf: true, frequency: "monthly",
                                     deliver_mode: "email",  // email | list
                                     recipient_email: "", recipient_list_id: "" });
  const [err, setErr] = useState("");

  const load = () => client.get("/admin/regulatory-bundle/schedules").then((r) => setSchedules(r.data?.schedules || [])).catch((e) => setErr(apiErr(e)));

  useEffect(() => {
    load();
    client.get("/export/xml/types").then((r) => setAvailable(r.data?.types || [])).catch(() => {});
  }, []);

  const create = async () => {
    setBusy(true); setErr("");
    try {
      if (!form.name || form.types.length === 0) {
        setErr("Name and at least one collection type are required."); setBusy(false); return;
      }
      const payload = {
        name: form.name, kind: form.kind, types: form.types,
        include_networth_pdf: form.include_networth_pdf, frequency: form.frequency,
      };
      if (form.deliver_mode === "list") {
        if (!form.recipient_list_id) { setErr("Pick a distribution list."); setBusy(false); return; }
        payload.recipient_list_id = form.recipient_list_id;
      } else {
        if (!form.recipient_email) { setErr("Recipient email is required."); setBusy(false); return; }
        payload.recipient_email = form.recipient_email;
      }
      await client.post("/admin/regulatory-bundle/schedules", payload);
      setShowAdd(false); setForm({ ...form, name: "", recipient_email: "", recipient_list_id: "" });
      load();
    } catch (e) { setErr(apiErr(e)); }
    setBusy(false);
  };

  const cancel = async (id) => {
    if (!window.confirm("Cancel this schedule? Future runs will stop.")) return;
    try { await client.delete(`/admin/regulatory-bundle/schedules/${id}`); load(); } catch (e) { setErr(apiErr(e)); }
  };

  return (
    <Card className="space-y-3" data-testid="bundle-scheduler-panel">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 font-head text-lg"><Clock size={18} className="text-[var(--primary)]" /> Bundle scheduler</div>
        <Button className="ms-auto" onClick={() => setShowAdd(true)} data-testid="bundle-scheduler-add"><Calendar size={15} /> New schedule</Button>
      </div>
      <p className="text-sm opacity-60">
        Auto-generate <b>Regulatory bundles</b> (XML + JSON + MANIFEST) or <b>Board-Ready Snapshots</b> (signed with Ed25519, anchored to RFC 3161) on a recurring cadence and email them to a compliance mailbox.
      </p>
      {err && <div className="rounded-md border border-[#DA1E28]/40 bg-[#DA1E28]/10 px-3 py-2 text-sm text-[#DA1E28]" data-testid="bundle-scheduler-error">{err}</div>}
      {schedules.length === 0 ? (
        <div className="rounded-md border border-dashed border-black/10 dark:border-white/10 p-4 text-sm opacity-60 text-center" data-testid="bundle-scheduler-empty">
          No schedules yet. Create one to auto-ship monthly / quarterly / annual evidence packs.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="bundle-scheduler-list">
          {schedules.map((s) => (
            <li key={s.id} className="flex flex-wrap items-center gap-2 rounded-md border border-black/10 dark:border-white/10 p-2 text-sm" data-testid={`bundle-schedule-${s.id}`}>
              <span className="font-medium">{s.name}</span>
              <Badge color={s.kind === "board_snapshot" ? "success" : "info"} data-testid={`bundle-schedule-kind-${s.id}`}>
                {s.kind === "board_snapshot" ? "board snapshot" : "regulatory"}
              </Badge>
              <Badge color={s.status === "active" ? "success" : s.status === "paused" ? "warn" : "neutral"} data-testid={`bundle-schedule-status-${s.id}`}>{s.status}</Badge>
              <Badge color="info">{s.frequency}</Badge>
              <span className="opacity-60 text-xs">→ {s.recipient_list_id
                ? (() => { const l = recipientLists.find((x) => x.id === s.recipient_list_id); return l ? `list · ${l.name} (${l.recipients.length})` : `list · ${s.recipient_list_id}`; })()
                : s.recipient_email}</span>
              <span className="opacity-60 text-xs">Next: {new Date(s.next_run_at).toLocaleString()}</span>
              <span className="opacity-60 text-xs">{s.types.length} types{s.include_networth_pdf ? " + PDF" : ""}</span>
              {s.runs > 0 && <span className="opacity-60 text-xs">{s.runs} run(s)</span>}
              {s.status !== "cancelled" && <Button variant="ghost" className="ms-auto" onClick={() => cancel(s.id)} data-testid={`bundle-schedule-cancel-${s.id}`}><X size={13} /> Cancel</Button>}
            </li>
          ))}
        </ul>
      )}

      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="New bundle schedule" testid="bundle-scheduler-modal">
        <div className="space-y-3">
          <div>
            <div className="mb-1 text-sm font-medium">Kind</div>
            <div className="grid grid-cols-2 gap-2">
              <label className={`cursor-pointer rounded-md border p-2 text-sm ${form.kind === "regulatory" ? "border-[var(--primary)] bg-[var(--primary)]/5" : "border-black/10 dark:border-white/10"}`}>
                <input type="radio" name="sched-kind" value="regulatory" checked={form.kind === "regulatory"}
                  onChange={() => setForm({ ...form, kind: "regulatory" })}
                  data-testid="bundle-scheduler-kind-regulatory" className="me-1" />
                <b>Regulatory bundle</b>
                <div className="text-xs opacity-70">XML + JSON + MANIFEST (SHA-256).</div>
              </label>
              <label className={`cursor-pointer rounded-md border p-2 text-sm ${form.kind === "board_snapshot" ? "border-[var(--primary)] bg-[var(--primary)]/5" : "border-black/10 dark:border-white/10"}`}>
                <input type="radio" name="sched-kind" value="board_snapshot" checked={form.kind === "board_snapshot"}
                  onChange={() => setForm({ ...form, kind: "board_snapshot", include_networth_pdf: true })}
                  data-testid="bundle-scheduler-kind-board" className="me-1" />
                <b>Board-Ready Snapshot</b>
                <div className="text-xs opacity-70">Bundle + cover-letter PDF, Ed25519-signed and RFC 3161 anchored.</div>
              </label>
            </div>
          </div>
          <div>
            <div className="mb-1 text-sm font-medium">Name</div>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="bundle-scheduler-name"
              placeholder={form.kind === "board_snapshot" ? "e.g. Quarterly board pack" : "e.g. Monthly compliance pack — Alberta"}
              className="w-full rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 text-sm" />
          </div>
          <div>
            <div className="mb-1 text-sm font-medium">Deliver to</div>
            <div className="grid grid-cols-2 gap-2">
              <label className={`cursor-pointer rounded-md border p-2 text-sm ${form.deliver_mode === "email" ? "border-[var(--primary)] bg-[var(--primary)]/5" : "border-black/10 dark:border-white/10"}`}>
                <input type="radio" name="deliver-mode" value="email" checked={form.deliver_mode === "email"}
                  onChange={() => setForm({ ...form, deliver_mode: "email" })}
                  data-testid="bundle-scheduler-deliver-email" className="me-1" />
                Single recipient
              </label>
              <label className={`cursor-pointer rounded-md border p-2 text-sm ${form.deliver_mode === "list" ? "border-[var(--primary)] bg-[var(--primary)]/5" : "border-black/10 dark:border-white/10"} ${recipientLists.length === 0 ? "opacity-40 pointer-events-none" : ""}`}>
                <input type="radio" name="deliver-mode" value="list" checked={form.deliver_mode === "list"}
                  onChange={() => setForm({ ...form, deliver_mode: "list" })}
                  disabled={recipientLists.length === 0}
                  data-testid="bundle-scheduler-deliver-list" className="me-1" />
                Distribution list {recipientLists.length === 0 ? "(none saved yet)" : `(${recipientLists.length})`}
              </label>
            </div>
          </div>
          {form.deliver_mode === "email" ? (
            <div>
              <div className="mb-1 text-sm font-medium">Recipient email</div>
              <input type="email" value={form.recipient_email} onChange={(e) => setForm({ ...form, recipient_email: e.target.value })} data-testid="bundle-scheduler-email"
                placeholder="compliance@yourdomain.com"
                className="w-full rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 text-sm" />
            </div>
          ) : (
            <div>
              <div className="mb-1 text-sm font-medium">Distribution list</div>
              <select value={form.recipient_list_id}
                onChange={(e) => setForm({ ...form, recipient_list_id: e.target.value })}
                data-testid="bundle-scheduler-list"
                className="w-full rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 text-sm">
                <option value="">— Select a list —</option>
                {recipientLists.map((l) => (
                  <option key={l.id} value={l.id}>{l.name} · {l.recipients.length} recipient{l.recipients.length === 1 ? "" : "s"}</option>
                ))}
              </select>
              {form.recipient_list_id && (() => {
                const l = recipientLists.find((x) => x.id === form.recipient_list_id);
                if (!l) return null;
                return <div className="mt-1 text-xs opacity-70" data-testid="bundle-scheduler-list-preview">Will email {l.recipients.length} recipient{l.recipients.length === 1 ? "" : "s"}: {l.recipients.map((r) => r.name || r.email).join(", ")}</div>;
              })()}
            </div>
          )}
          <div>
            <div className="mb-1 text-sm font-medium">Frequency</div>
            <select value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })} data-testid="bundle-scheduler-frequency"
              className="rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 text-sm">
              <option value="once">One-shot</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="annual">Annual</option>
            </select>
          </div>
          <div>
            <div className="mb-1 text-sm font-medium">Include collections</div>
            <div className="flex flex-wrap gap-2">
              {(available.length ? available : [{ type: "holdings", records: "?" }]).map((t) => (
                <label key={t.type} className={`flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${form.types.includes(t.type) ? "border-[var(--primary)] bg-[var(--primary)]/5" : "border-black/10 dark:border-white/10"}`}>
                  <input type="checkbox" checked={form.types.includes(t.type)} data-testid={`bundle-scheduler-type-${t.type}`}
                    onChange={(e) => setForm({ ...form, types: e.target.checked ? [...form.types, t.type] : form.types.filter((x) => x !== t.type) })} />
                  {t.type} <span className="opacity-50">({t.records})</span>
                </label>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.include_networth_pdf} onChange={(e) => setForm({ ...form, include_networth_pdf: e.target.checked })} data-testid="bundle-scheduler-pdf" />
            Include branded Net-Worth Statement PDF
          </label>
          {err && <div className="text-sm text-red-500" data-testid="bundle-scheduler-modal-error">{err}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setShowAdd(false)} data-testid="bundle-scheduler-cancel-btn">Cancel</Button>
            <Button onClick={create} disabled={busy} data-testid="bundle-scheduler-save">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Create schedule
            </Button>
          </div>
        </div>
      </Modal>
    </Card>
  );
}


// ==================== Bundle History ====================
function BundleHistoryPanel() {
  const [rows, setRows] = useState([]);
  useEffect(() => { client.get("/admin/regulatory-bundle/history?limit=25").then((r) => setRows(r.data?.bundles || [])).catch(() => {}); }, []);
  return (
    <Card className="space-y-3" data-testid="bundle-history-panel">
      <div className="flex items-center gap-2 font-head text-lg"><FileArchive size={18} className="text-[var(--primary)]" /> Bundle history</div>
      <p className="text-sm opacity-60">Every regulatory bundle you've generated (manual + scheduled). Bundle IDs are searchable via the hash-lookup panel below.</p>
      {rows.length === 0 ? (
        <div className="rounded-md border border-dashed border-black/10 dark:border-white/10 p-4 text-sm opacity-60 text-center" data-testid="bundle-history-empty">No bundles generated yet.</div>
      ) : (
        <ul className="space-y-1 text-sm" data-testid="bundle-history-list">
          {rows.slice(0, 25).map((r, i) => (
            <li key={r.bundle_id + i} className="rounded-md border border-black/10 dark:border-white/10 p-2" data-testid={`bundle-history-row-${i}`}>
              <div className="flex flex-wrap items-center gap-2">
                <code className="text-xs">{r.bundle_id}</code>
                <span className="opacity-60 text-xs">{new Date(r.created_at).toLocaleString()}</span>
                <Badge color="info">{r.files} files</Badge>
                {r.included_pdf && <Badge color="success">+PDF</Badge>}
                <span className="opacity-60 text-xs ms-auto">{(r.types || []).join(", ")}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}


// ==================== PDF Audit / Hash Lookup ====================
function HashLookupPanel() {
  const [hash, setHash] = useState("");
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const lookup = async () => {
    setBusy(true); setErr(""); setResult(null);
    try {
      const { data } = await client.get(`/admin/pdf-audit/${encodeURIComponent(hash.trim().toUpperCase())}`);
      setResult(data);
    } catch (e) { setErr(apiErr(e)); }
    setBusy(false);
  };
  return (
    <Card className="space-y-3" data-testid="hash-lookup-panel">
      <div className="flex items-center gap-2 font-head text-lg"><Search size={18} className="text-[var(--primary)]" /> PDF viewer-hash lookup</div>
      <p className="text-sm opacity-60">Given the 12-character ID printed on a leaked PDF (in the diagonal watermark), trace it back to the recipient and timestamp.</p>
      <div className="flex flex-wrap items-center gap-2">
        <input value={hash} onChange={(e) => setHash(e.target.value)} data-testid="hash-lookup-input"
          placeholder="e.g. 69F7B922A4FA"
          className="grow rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 text-sm font-mono" />
        <Button onClick={lookup} disabled={busy || !hash.trim()} data-testid="hash-lookup-btn">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />} Lookup
        </Button>
      </div>
      {err && <div className="rounded-md border border-[#DA1E28]/40 bg-[#DA1E28]/10 px-3 py-2 text-sm text-[#DA1E28]" data-testid="hash-lookup-error">{err}</div>}
      {result && (
        <div className="rounded-md border border-[#24A148]/40 bg-[#24A148]/10 p-3 text-sm" data-testid="hash-lookup-result">
          <div className="font-medium text-[#24A148]">✓ Match found</div>
          <div className="mt-1 space-y-1 text-xs">
            <div><b>Recipient:</b> {result.recipient}</div>
            <div><b>Downloaded by:</b> {result.downloaded_by}</div>
            <div><b>Document:</b> {result.document}</div>
            <div><b>Timestamp:</b> {result.timestamp}</div>
          </div>
        </div>
      )}
    </Card>
  );
}


// ==================== Bundle Verify ====================
function BundleVerifyPanel() {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const verify = async () => {
    if (!file) return;
    setBusy(true); setErr(""); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await client.post("/verify-bundle", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
    } catch (e) { setErr(apiErr(e)); }
    setBusy(false);
  };
  return (
    <Card className="space-y-3" data-testid="bundle-verify-panel">
      <div className="flex items-center gap-2 font-head text-lg"><ShieldCheck size={18} className="text-[var(--primary)]" /> Verify bundle integrity</div>
      <p className="text-sm opacity-60">Drop any regulatory bundle ZIP here — public endpoint, no auth needed. Every file is rehashed and compared to MANIFEST.txt. Shareable link: <code>/verify</code>.</p>
      <div className="flex flex-wrap items-center gap-2">
        <input type="file" accept=".zip" onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }} data-testid="bundle-verify-file"
          className="text-sm" />
        <Button onClick={verify} disabled={busy || !file} data-testid="bundle-verify-btn">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <ShieldCheck size={15} />} Verify
        </Button>
      </div>
      {err && <div className="rounded-md border border-[#DA1E28]/40 bg-[#DA1E28]/10 px-3 py-2 text-sm text-[#DA1E28]" data-testid="bundle-verify-error">{err}</div>}
      {result && (
        <div className={`rounded-md border p-3 text-sm ${result.ok ? "border-[#24A148]/40 bg-[#24A148]/10" : "border-[#DA1E28]/40 bg-[#DA1E28]/10"}`} data-testid="bundle-verify-result">
          <div className={`flex items-center gap-2 font-medium ${result.ok ? "text-[#24A148]" : "text-[#DA1E28]"}`}>
            {result.ok ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
            {result.ok ? "Bundle is intact — every file matches its MANIFEST hash." : `Tampering detected — ${result.total_files - result.verified_files} of ${result.total_files} files differ.`}
          </div>
          <div className="mt-1 text-xs opacity-70">Bundle ID: <b>{result.bundle_id || "—"}</b> · Generated: {result.generated_at || "—"}</div>
          <ul className="mt-2 space-y-1 text-xs" data-testid="bundle-verify-list">
            {result.results.map((r, i) => (
              <li key={i} className={`flex items-center gap-2 ${r.status === "ok" ? "opacity-70" : r.status === "tampered" ? "text-[#DA1E28] font-medium" : "text-[#F1C21B]"}`} data-testid={`bundle-verify-row-${i}`}>
                {r.status === "ok" ? "✓" : r.status === "tampered" ? "✗ TAMPERED" : "? MISSING"} <code className="text-[10px] opacity-70">{(r.expected || "").slice(0, 12)}…</code> {r.filename}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}


// ==================== Board-Ready Snapshot ====================
function BoardSnapshotPanel() {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const download = async () => {
    setBusy(true); setMsg("");
    try {
      const res = await client.get("/admin/board-snapshot", { responseType: "blob" });
      const bid = res.headers["x-bundle-id"] || "";
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `board-snapshot-${bid}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setMsg(`✓ Downloaded board snapshot ${bid}. Includes cover-letter PDF, regulatory bundle, and metrics.json.`);
    } catch (e) { setMsg(apiErr(e)); }
    setBusy(false);
  };
  return (
    <Card className="space-y-3" data-testid="board-snapshot-panel">
      <div className="flex items-center gap-2 font-head text-lg"><Download size={18} className="text-[var(--primary)]" /> Board-Ready Snapshot</div>
      <p className="text-sm opacity-60">A single ZIP with a one-page executive cover letter (branded PDF), the full regulatory bundle, and a machine-readable metrics.json. Ready to hand to a board or auditor.</p>
      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={download} disabled={busy} data-testid="board-snapshot-btn">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Download board snapshot
        </Button>
        {msg && <span className="text-xs opacity-70" data-testid="board-snapshot-msg">{msg}</span>}
      </div>
    </Card>
  );
}


// ==================== Manual bundle export (reuses existing button) ====================
function ManualBundlePanel() {
  return (
    <Card className="space-y-3" data-testid="manual-bundle-panel">
      <div className="flex items-center gap-2 font-head text-lg"><FileArchive size={18} className="text-[var(--primary)]" /> One-off regulatory bundle</div>
      <p className="text-sm opacity-60">Instant, ad-hoc bundle download. Choose which collections to include and whether to embed the branded net-worth PDF.</p>
      <RegulatoryBundleButton />
    </Card>
  );
}


// ==================== Page ====================
export default function ReportsCompliance() {
  const [recipientLists, setRecipientLists] = useState([]);
  const [customFilingsBump, setCustomFilingsBump] = useState(0);
  return (
    <div className="mx-auto max-w-6xl space-y-6 p-4" data-testid="reports-compliance-page">
      <div className="space-y-1">
        <h1 className="font-head text-2xl">Reports &amp; Compliance</h1>
        <p className="text-sm opacity-60">
          Everything a regulator or board will ever ask for — evidence bundles, auto-scheduled deliveries, tamper-proof integrity checks, PDF traceability, and one-touch executive snapshots.
        </p>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <ManualBundlePanel />
        <BoardSnapshotPanel />
      </div>
      <ComplianceCalendarPanel onFilingsReload={customFilingsBump} />
      <CustomFilingsPanel onFilingsChange={() => setCustomFilingsBump((n) => n + 1)} />
      <DelegationRosterPanel />
      <DelegationHeatMapPanel />
      <BulkRetireDelegationsPanel />
      <BoardRecipientListsPanel onListsChange={setRecipientLists} />
      <BundleSchedulerPanel recipientLists={recipientLists} />
      <ProvableTimelinePanel />
      <SigningKeyPanel />
      <BundleHistoryPanel />
      <div className="grid gap-4 md:grid-cols-2">
        <HashLookupPanel />
        <BundleVerifyPanel />
      </div>
    </div>
  );
}
